import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import DataTable from "../components/DataTable";
import EntityForm from "../components/EntityForm";
import type { CrudColumn, CrudField } from "../types";
import { useDeals } from "../services/dealsService";
import { quoteEditorPath } from "../services/quotePaths";
import { fetchBudgets } from "../../expenses/services/expensesService";
import { budgetLinesFor } from "../../expenses/lib/budget";
import {
  ISSUE_LABEL,
  apiErrorMessage,
  cancelInvoice,
  createInvoice,
  downloadDraftPdf,
  downloadInvoicePdf,
  draftIsComplete,
  fetchInvoices,
  fetchUpcomingInvoices,
  invoiceMoney,
  markInvoicePaid,
  periodBilling,
  periodLabel,
  resendPaymentConfirmation,
  sendDraftToMe,
  sendInvoice,
  statusLabel,
  type Invoice,
  type UpcomingInvoice,
} from "../services/invoicesService";

// ===========================================================================
// Negocios → Facturas (LIVE, `crm_invoices`).
// ===========================================================================
// Las facturas de lo que nos pagan los clientes: las pasadas y las del periodo
// en curso. Cada negocio con cobro configurado genera su factura en la fecha de
// corte y se manda sola; cuando entra el ingreso (o aquí se marca pagada) el
// cliente recibe la confirmación de pago. Todo ingreso de un cliente tiene su
// factura.
//
// Arriba de la lista va «Por emitir»: los borradores del mes en curso (lo que la
// corrida de corte va a generar y mandar) y, para quien puede ver Gastos, lo
// facturado del mes contra el presupuesto de ese mes.
// ===========================================================================

type Row = Invoice & Record<string, unknown>;
type Filter = "todas" | "periodo" | "por-cobrar" | "vencidas" | "pagadas";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "todas", label: "Todas" },
  { id: "periodo", label: "Periodo actual" },
  { id: "por-cobrar", label: "Por cobrar" },
  { id: "vencidas", label: "Vencidas" },
  { id: "pagadas", label: "Pagadas" },
];

const SOURCE_LABEL: Record<Invoice["source"], string> = { corte: "Fecha de corte", ingreso: "Ingreso", manual: "Manual", cotizacion: "Cotización aceptada" };

const STATUS_TONE: Record<string, string> = {
  Pagada: "bg-emerald-500/15 text-emerald-600",
  Enviada: "bg-sky-500/15 text-sky-600",
  Parcial: "bg-amber-500/15 text-amber-600",
  Pendiente: "bg-gray-400/15 text-gray-500",
  Vencida: "bg-red-500/15 text-red-500",
  Cancelada: "bg-gray-400/15 text-gray-400 line-through",
};

const shortDate = (iso: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
/** Mes en curso en Ciudad de México ("2026-10"), como el backend. */
const currentMonth = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City", year: "numeric", month: "2-digit" }).format(new Date()).slice(0, 7);

const isOpen = (i: Invoice) => i.status !== "pagada" && i.status !== "cancelada";

export function filterInvoices(invoices: Invoice[], filter: Filter, month = currentMonth()): Invoice[] {
  switch (filter) {
    case "periodo":
      return invoices.filter((i) => i.period.startsWith(month));
    case "por-cobrar":
      return invoices.filter(isOpen);
    case "vencidas":
      return invoices.filter((i) => i.overdue);
    case "pagadas":
      return invoices.filter((i) => i.status === "pagada");
    default:
      return invoices;
  }
}

export default function FacturasSection() {
  const { isDarkMode } = useTheme();
  const themeClasses = buildThemeClasses(isDarkMode);
  const deals = useDeals();
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("todas");
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmPaid, setConfirmPaid] = useState<Invoice | null>(null);
  const [detail, setDetail] = useState<Invoice | null>(null);
  const [drafts, setDrafts] = useState<UpcomingInvoice[] | null>(null);
  /** Presupuesto de gastos del mes. null = sin acceso a Gastos o sin presupuesto: no se compara. */
  const [monthBudget, setMonthBudget] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setInvoices(await fetchInvoices());
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err));
      setInvoices((prev) => prev ?? []);
    }
    // Los borradores y el presupuesto son un extra: si fallan, la lista sigue.
    try {
      setDrafts((await fetchUpcomingInvoices()).drafts);
    } catch {
      setDrafts([]);
    }
    try {
      const lines = budgetLinesFor(await fetchBudgets(), currentMonth());
      setMonthBudget(lines.length ? Math.round(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0) * 100) / 100 : null);
    } catch {
      setMonthBudget(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const replace = (next: Invoice) => setInvoices((prev) => (prev ?? []).map((i) => (i.id === next.id ? next : i)));

  /** Corre una acción sobre una factura y deja el resultado (o el motivo del fallo) a la vista. */
  const run = async (invoice: Invoice, action: () => Promise<string>) => {
    setBusyId(invoice.id);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
    } catch (err) {
      setError(`${invoice.number}: ${apiErrorMessage(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const onSend = (invoice: Invoice) =>
    run(invoice, async () => {
      const next = await sendInvoice(invoice.id);
      replace(next);
      const last = [...next.deliveries].reverse().find((d) => d.type === "factura" && d.ok);
      return `Factura ${next.number} enviada a ${(last?.to.length ? last.to : next.emails).join(", ")}`;
    });

  const onMarkPaid = (invoice: Invoice) =>
    run(invoice, async () => {
      const result = await markInvoicePaid(invoice.id);
      replace(result.invoice);
      return result.notified
        ? `Factura ${invoice.number} pagada. Se envió la confirmación de pago al cliente.`
        : `Factura ${invoice.number} pagada. La confirmación no se envió: ${result.notifyReason ?? "el cliente no tiene a nadie con correo en el CRM"}.`;
    });

  const onResend = (invoice: Invoice) =>
    run(invoice, async () => {
      replace(await resendPaymentConfirmation(invoice.id));
      return `Confirmación de pago de ${invoice.number} enviada`;
    });

  const onCancel = (invoice: Invoice) =>
    run(invoice, async () => {
      replace(await cancelInvoice(invoice.id));
      return `Factura ${invoice.number} cancelada`;
    });

  /** Acción sobre un borrador: no cambia nada guardado, solo avisa cómo le fue. */
  const runDraft = async (draft: UpcomingInvoice, action: () => Promise<string>) => {
    setBusyId(draft.id);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
    } catch (err) {
      setError(`Borrador de ${draft.client}: ${apiErrorMessage(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const onDraftPdf = (draft: UpcomingInvoice) =>
    runDraft(draft, async () => {
      await downloadDraftPdf(draft);
      return `PDF del borrador de ${draft.client} descargado`;
    });

  const onDraftSend = (draft: UpcomingInvoice) =>
    runDraft(draft, async () => {
      const to = await sendDraftToMe(draft.id);
      return `Borrador de ${draft.client} enviado a tu correo${to ? ` (${to})` : ""}. Al cliente no se le envió nada.`;
    });

  const onPdf = (invoice: Invoice) =>
    run(invoice, async () => {
      await downloadInvoicePdf(invoice);
      return `PDF de ${invoice.number} descargado`;
    });

  const all = useMemo(() => invoices ?? [], [invoices]);
  const month = currentMonth();
  const totals = useMemo(() => {
    const active = all.filter((i) => i.status !== "cancelada");
    return {
      open: active.filter(isOpen).reduce((s, i) => s + i.balance, 0),
      openCount: active.filter(isOpen).length,
      overdue: active.filter((i) => i.overdue).reduce((s, i) => s + i.balance, 0),
      overdueCount: active.filter((i) => i.overdue).length,
      period: active.filter((i) => i.period.startsWith(month)).reduce((s, i) => s + i.amount, 0),
      periodCount: active.filter((i) => i.period.startsWith(month)).length,
      paid: active.reduce((s, i) => s + i.paidAmount, 0),
    };
  }, [all, month]);

  const upcoming = useMemo(() => drafts ?? [], [drafts]);
  const billing = useMemo(() => periodBilling(all, upcoming, month), [all, upcoming, month]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return filterInvoices(all, filter, month).filter(
      (i) => !q || [i.number, i.client, i.payer, i.dealTitle, i.concept, i.quoteFolio].some((v) => String(v ?? "").toLowerCase().includes(q)),
    ) as Row[];
  }, [all, filter, month, query]);

  const columns: CrudColumn<Row>[] = [
    {
      key: "number",
      label: "Folio",
      render: (r) => (
        <div>
          <button type="button" className="whitespace-nowrap font-semibold text-primary-500 hover:underline" onClick={() => setDetail(r)}>
            {r.number}
          </button>
          {/* Nació de una cotización aceptada (#1546): su folio y la liga al armador. */}
          {r.quoteId && (
            <a href={quoteEditorPath(r.quoteId)} className={`block whitespace-nowrap text-[11px] hover:underline ${themeClasses.textSecondary}`} data-testid={`invoice-quote-${r.id}`}>
              {r.quoteFolio || "Cotización"}
            </a>
          )}
        </div>
      ),
    },
    {
      key: "client",
      label: "Cliente",
      render: (r) => (
        <div>
          <div>{r.client}</div>
          {r.payer && r.payer !== r.client && <div className={`text-[11px] ${themeClasses.textSecondary}`}>{r.payer}</div>}
        </div>
      ),
    },
    { key: "period", label: "Periodo", render: (r) => <span className="whitespace-nowrap">{periodLabel(r.period)}</span> },
    { key: "issueDate", label: "Emisión", render: (r) => shortDate(r.issueDate) },
    { key: "dueDate", label: "Vence", render: (r) => shortDate(r.dueDate) },
    { key: "amount", label: "Total", align: "right", render: (r) => <span className="whitespace-nowrap tabular-nums">{invoiceMoney(r.amount, r.currency)}</span> },
    {
      key: "balance",
      label: "Saldo",
      align: "right",
      render: (r) => <span className="whitespace-nowrap tabular-nums">{r.status === "cancelada" ? "—" : invoiceMoney(r.balance, r.currency)}</span>,
    },
    {
      key: "status",
      label: "Estado",
      align: "center",
      render: (r) => {
        const label = statusLabel(r);
        return <span className={`inline-block whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ${STATUS_TONE[label] ?? ""}`}>{label}</span>;
      },
    },
    {
      key: "sentAt",
      label: "Envíos",
      render: (r) => (
        <div className={`whitespace-nowrap text-[11px] ${themeClasses.textSecondary}`}>
          <div title="Factura enviada al cliente">
            <i className="ri-mail-send-line mr-1" />
            {r.sentAt ? shortDate(r.sentAt.slice(0, 10)) : "Sin enviar"}
          </div>
          {r.paidAmount > 0 && (
            <div title="Confirmación de pago enviada al cliente">
              <i className="ri-checkbox-circle-line mr-1" />
              {r.paymentNotifiedAt ? shortDate(r.paymentNotifiedAt.slice(0, 10)) : "Sin confirmar"}
            </div>
          )}
        </div>
      ),
    },
  ];

  const actionButton = (title: string, icon: string, tone: string, onClick: () => void, disabled = false) => (
    <button type="button" title={title} aria-label={title} disabled={disabled} onClick={onClick} className={`rounded-md p-1.5 disabled:opacity-40 ${tone}`}>
      <i className={icon} />
    </button>
  );

  const renderActions = (r: Row) => {
    const busy = busyId === r.id;
    const open = isOpen(r);
    return (
      <div className="flex items-center justify-center gap-0.5">
        {actionButton("Descargar PDF", "ri-file-pdf-2-line", "text-primary-500 hover:bg-primary-500/10", () => onPdf(r), busy)}
        {r.status !== "cancelada" &&
          actionButton(r.sentAt ? "Reenviar factura al cliente" : "Enviar factura al cliente", "ri-mail-send-line", "text-sky-500 hover:bg-sky-500/10", () => onSend(r), busy)}
        {open && actionButton("Marcar pagada y confirmar al cliente", "ri-checkbox-circle-line", "text-emerald-600 hover:bg-emerald-500/10", () => setConfirmPaid(r), busy)}
        {r.paidAmount > 0 &&
          r.status !== "cancelada" &&
          actionButton("Enviar confirmación de pago", "ri-mail-check-line", "text-emerald-600 hover:bg-emerald-500/10", () => onResend(r), busy)}
        {open && !r.payments.some((p) => p.incomeId) && actionButton("Cancelar factura", "ri-close-circle-line", "text-red-500 hover:bg-red-500/10", () => onCancel(r), busy)}
      </div>
    );
  };

  const createFields: CrudField[] = [
    {
      key: "dealId",
      label: "Negocio",
      type: "select",
      options: [{ value: "", label: "Sin negocio" }, ...deals.map((d) => ({ value: d.id, label: d.title }))],
    },
    { key: "client", label: "Cliente (si no hay negocio)", placeholder: "Dumax" },
    { key: "concept", label: "Concepto", placeholder: "Desarrollo — octubre 2026" },
    { key: "amount", label: "Total (MXN)", type: "number", required: true, placeholder: "26000" },
    { key: "issueDate", label: "Emisión", type: "date" },
    { key: "dueDate", label: "Vence", type: "date" },
    { key: "emails", label: "Correos a los que se manda (vacío = las personas del cliente en el CRM)", type: "textarea" },
  ];

  const submitCreate = async (values: Record<string, string>) => {
    setCreating(false);
    setError(null);
    try {
      const created = await createInvoice({
        dealId: values.dealId || null,
        client: values.client || undefined,
        concept: values.concept || undefined,
        amount: Number(values.amount),
        issueDate: values.issueDate || undefined,
        dueDate: values.dueDate || undefined,
        emails: values.emails ? values.emails.split(/[\s,;]+/).filter(Boolean) : undefined,
      });
      setInvoices((prev) => [created, ...(prev ?? [])]);
      setNotice(`Factura ${created.number} creada`);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const kpi = (label: string, value: string, hint: string, tone = "") => (
    <div className={`rounded-2xl border p-4 ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
      <p className={`text-xs ${themeClasses.textSecondary}`}>{label}</p>
      <p className={`mt-1 text-lg font-bold tabular-nums ${tone || themeClasses.text}`}>{value}</p>
      <p className={`text-[11px] ${themeClasses.textSecondary}`}>{hint}</p>
    </div>
  );

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-bill-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${themeClasses.text}`}>Facturas</h1>
              <p className={`text-sm ${themeClasses.textSecondary}`}>Lo que nos pagan los clientes: facturas pasadas y del periodo en curso</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${themeClasses.border} ${themeClasses.cardBg}`}>
              <i className={`ri-search-line ${themeClasses.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar folio o cliente"
                aria-label="Buscar folio o cliente"
                className={`bg-transparent text-sm outline-none ${themeClasses.text}`}
              />
            </div>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400"
            >
              <i className="ri-add-line" />
              <span className="whitespace-nowrap">Nueva factura</span>
            </button>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {kpi("Por cobrar", invoiceMoney(totals.open), `${totals.openCount} factura(s) con saldo`)}
          {kpi("Vencido", invoiceMoney(totals.overdue), `${totals.overdueCount} factura(s) vencida(s)`, totals.overdue > 0 ? "text-red-500" : "")}
          {kpi("Periodo actual", invoiceMoney(billing.total), `${totals.periodCount} emitida(s) y ${upcoming.length} borrador(es) de ${periodLabel(month)}`)}
          {kpi("Cobrado", invoiceMoney(totals.paid), "Pagos aplicados a facturas")}
        </div>

        {error && (
          <p role="alert" className="mb-3 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">
            <i className="ri-error-warning-line mr-1" />
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="mb-3 rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600">
            <i className="ri-checkbox-circle-line mr-1" />
            {notice}
          </p>
        )}

        <section aria-label="Facturas por emitir" className={`mb-4 rounded-2xl border p-4 ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h2 className={`text-sm font-semibold ${themeClasses.text}`}>Por emitir en {periodLabel(month)}</h2>
              <p className={`text-xs ${themeClasses.textSecondary}`}>Borradores: todavía no tienen folio. Cada uno se genera y se manda solo en su fecha de corte. Puedes descargar su PDF o mandártelo a tu correo para revisarlo.</p>
            </div>
            {monthBudget != null && (
              <p className={`text-xs ${themeClasses.textSecondary}`} data-testid="budget-match">
                Facturación del mes <strong className={themeClasses.text}>{invoiceMoney(billing.total)}</strong> (emitido {invoiceMoney(billing.issued)} + borradores{" "}
                {invoiceMoney(billing.drafted)}) · Presupuesto de gastos <strong className={themeClasses.text}>{invoiceMoney(monthBudget)}</strong> ·{" "}
                <span className={billing.total >= monthBudget ? "font-semibold text-emerald-600" : "font-semibold text-red-500"}>
                  {billing.total >= monthBudget
                    ? `Cubre el presupuesto (+${invoiceMoney(billing.total - monthBudget)})`
                    : `Faltan ${invoiceMoney(monthBudget - billing.total)} para cubrirlo`}
                </span>
                {billing.withoutAmount > 0 && ` · ${billing.withoutAmount} borrador(es) sin monto no suman`}
              </p>
            )}
          </div>
          {drafts === null ? (
            <p className={`text-sm ${themeClasses.textSecondary}`}>Cargando borradores…</p>
          ) : upcoming.length === 0 ? (
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              No hay facturas por emitir este mes. Para que un negocio se facture solo, captura su cobro en Negocios → editar → Cobro.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`text-left text-xs ${themeClasses.textSecondary}`}>
                    <th className="pb-2 pr-3 font-medium">Cliente</th>
                    <th className="pb-2 pr-3 font-medium">Periodo</th>
                    <th className="pb-2 pr-3 font-medium">Se emite</th>
                    <th className="pb-2 pr-3 text-right font-medium">Total</th>
                    <th className="pb-2 pr-3 font-medium">Se enviará a</th>
                    <th className="pb-2 pr-3 font-medium">Estado</th>
                    <th className="pb-2 text-center font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {upcoming.map((d) => (
                    <tr key={d.id} className={`border-t ${themeClasses.border} ${themeClasses.text}`}>
                      <td className="py-2 pr-3">
                        <div>{d.client}</div>
                        {d.dealTitle && d.dealTitle !== d.client && <div className={`text-[11px] ${themeClasses.textSecondary}`}>{d.dealTitle}</div>}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3">{periodLabel(d.period)}</td>
                      <td className="whitespace-nowrap py-2 pr-3">{d.issueDate ? shortDate(d.issueDate) : "Sin fecha de corte"}</td>
                      <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                        {d.amount != null ? (
                          invoiceMoney(d.amount, d.currency)
                        ) : (
                          <span className={themeClasses.textSecondary}>
                            Sin monto
                            {d.referenceAmount != null && d.referencePeriod ? ` (${periodLabel(d.referencePeriod)}: ${invoiceMoney(d.referenceAmount, d.currency)})` : ""}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs">{d.emails.join(", ") || "—"}</td>
                      <td className="py-2 pr-3">
                        <span className="inline-block whitespace-nowrap rounded-full bg-violet-500/15 px-2 py-1 text-xs font-medium text-violet-600">Borrador</span>
                        {d.issues.map((issue) => (
                          <div key={issue} className="mt-1 text-[11px] text-amber-600">
                            <i className="ri-alert-line mr-1" />
                            {ISSUE_LABEL[issue]}
                          </div>
                        ))}
                      </td>
                      <td className="py-2">
                        {draftIsComplete(d) ? (
                          <div className="flex items-center justify-center gap-0.5">
                            {actionButton("Descargar PDF del borrador", "ri-file-pdf-2-line", "text-primary-500 hover:bg-primary-500/10", () => onDraftPdf(d), busyId === d.id)}
                            {actionButton("Enviarme el borrador a mi correo", "ri-mail-send-line", "text-sky-500 hover:bg-sky-500/10", () => onDraftSend(d), busyId === d.id)}
                          </div>
                        ) : (
                          <div className={`text-center text-[11px] ${themeClasses.textSecondary}`}>Completa el cobro</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="mb-3 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={filter === f.id}
              onClick={() => setFilter(f.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filter === f.id ? "bg-primary-500 text-white" : `border ${themeClasses.border} ${themeClasses.textSecondary} hover:bg-primary-500/10`
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className={`rounded-2xl border p-4 ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
          {invoices === null ? (
            <p className={`p-6 text-center text-sm ${themeClasses.textSecondary}`}>Cargando facturas…</p>
          ) : (
            <DataTable rows={rows} columns={columns} isDarkMode={isDarkMode} renderActions={renderActions} pageSize={12} emptyText="No hay facturas con este filtro" />
          )}
        </div>

        <p className={`mt-3 text-xs ${themeClasses.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          La factura de cada periodo se genera y se manda sola en la fecha de corte del negocio (Negocios → editar → Cobro). Cuando entra un ingreso del
          cliente se aplica a su factura y se le confirma el pago; un ingreso sin factura abierta recibe la suya. No son CFDI.
        </p>
      </div>

      {creating && (
        <Modal
          title="Nueva factura"
          onClose={() => setCreating(false)}
          themeClasses={themeClasses}
          content={
            <EntityForm
              fields={createFields}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              submitLabel="Crear factura"
              onCancel={() => setCreating(false)}
              onSubmit={submitCreate}
            />
          }
        />
      )}

      {confirmPaid && (
        <Modal
          title={`Marcar pagada — ${confirmPaid.number}`}
          onClose={() => setConfirmPaid(null)}
          themeClasses={themeClasses}
          content={
            <div className="space-y-4">
              <p className={`text-sm ${themeClasses.text}`}>
                Se registra el pago del saldo de <strong>{invoiceMoney(confirmPaid.balance, confirmPaid.currency)}</strong> de {confirmPaid.client} y se le
                manda la confirmación de pago
                {confirmPaid.emails.length ? ` a ${confirmPaid.emails.join(", ")}` : " a las personas del cliente registradas en el CRM (Finanzas, Administración o Dirección)"}.
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" className={`rounded-lg border px-4 py-2 text-sm ${themeClasses.border} ${themeClasses.text}`} onClick={() => setConfirmPaid(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
                  onClick={() => {
                    const invoice = confirmPaid;
                    setConfirmPaid(null);
                    void onMarkPaid(invoice);
                  }}
                >
                  Marcar pagada
                </button>
              </div>
            </div>
          }
        />
      )}

      {detail && (
        <Modal
          title={`Factura ${detail.number}`}
          onClose={() => setDetail(null)}
          themeClasses={themeClasses}
          content={
            <div className={`space-y-4 text-sm ${themeClasses.text}`}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                {(
                  [
                    ["Cliente", detail.client],
                    ["Razón social", detail.payer ?? "—"],
                    ["Negocio", detail.dealTitle ?? "Sin negocio"],
                    ["Origen", detail.quoteFolio ? `${SOURCE_LABEL[detail.source] ?? detail.source} ${detail.quoteFolio}` : (SOURCE_LABEL[detail.source] ?? detail.source)],
                    ["Concepto", detail.concept || "—"],
                    ["Periodo", periodLabel(detail.period)],
                    ...detail.items.map((item) => [`Depósito a ${item.label}`, invoiceMoney(item.amount, detail.currency)] as const),
                    ["Total", invoiceMoney(detail.amount, detail.currency)],
                    ["Saldo", invoiceMoney(detail.balance, detail.currency)],
                    ["Se envió a", detail.emails.join(", ") || "—"],
                    ["Estado", statusLabel(detail)],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label}>
                    <dt className={`text-xs ${themeClasses.textSecondary}`}>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              {detail.quoteId && (
                <div data-testid="invoice-quote-lines">
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide">Renglones de la cotización</h3>
                    <a href={quoteEditorPath(detail.quoteId)} className="text-xs text-primary-500 hover:underline">
                      Abrir cotización {detail.quoteFolio ?? ""}
                    </a>
                  </div>
                  {(detail.lines ?? []).length === 0 ? (
                    <p className={`text-xs ${themeClasses.textSecondary}`}>Sin renglones.</p>
                  ) : (
                    <ul className="space-y-1 text-xs">
                      {(detail.lines ?? []).map((line, i) => (
                        <li key={`${line.name}-${i}`} className="flex justify-between gap-3">
                          <span>
                            {line.quantity} × {line.name}
                            {line.sku ? ` (${line.sku})` : ""}
                            {line.discountPct ? ` · −${line.discountPct} %` : ""}
                          </span>
                          <span className="tabular-nums">{invoiceMoney(line.amount, detail.currency)}</span>
                        </li>
                      ))}
                      {detail.taxTotal ? (
                        <li className={`flex justify-between gap-3 ${themeClasses.textSecondary}`}>
                          <span>IVA</span>
                          <span className="tabular-nums">{invoiceMoney(detail.taxTotal, detail.currency)}</span>
                        </li>
                      ) : null}
                    </ul>
                  )}
                </div>
              )}
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide">Pagos</h3>
                {detail.payments.length === 0 ? (
                  <p className={`text-xs ${themeClasses.textSecondary}`}>Sin pagos.</p>
                ) : (
                  <ul className="space-y-1 text-xs">
                    {detail.payments.map((p, i) => (
                      <li key={`${p.incomeId ?? "manual"}-${i}`} className="flex justify-between gap-3">
                        <span>
                          {shortDate(p.date)} · {p.payer ?? "—"} · {p.incomeId ? "Ingreso" : "Marcada a mano"}
                        </span>
                        <span className="tabular-nums">{invoiceMoney(p.amount, detail.currency)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide">Correos al cliente</h3>
                {detail.deliveries.length === 0 ? (
                  <p className={`text-xs ${themeClasses.textSecondary}`}>Aún no se ha enviado ningún correo.</p>
                ) : (
                  <ul className="space-y-1 text-xs">
                    {detail.deliveries.map((d, i) => (
                      <li key={`${d.at}-${i}`}>
                        {shortDate(d.at.slice(0, 10))} · {d.type === "pago" ? "Confirmación de pago" : "Factura"} ·{" "}
                        {d.ok ? `enviado a ${d.to.join(", ")}` : <span className="text-red-500">no enviado: {d.error}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
